export interface IndustryModuleMatrix {
  kvikSign: string;
  kvikID: string;
  kvikSend: string;
  kvikForm: string;
}

export interface IndustryChallengeSolution {
  title: string;
  challenge: string;
  solution: string;
}

export interface IndustryDetail {
  id: string;
  slug: string;
  englishSlug: string;
  number: number;
  seoTitle?: string;
  englishSeoTitle?: string;
  title: string;
  englishTitle: string;
  name: string;
  englishName: string;
  shortDescription: string;
  englishShortDescription: string;
  metaDescription: string;
  englishMetaDescription: string;
  heroImage: string;
  heroImageAlt: string;
  englishHeroImageAlt: string;
  introText: string;
  englishIntroText: string;
  challengeSolutions?: IndustryChallengeSolution[];
  benefits?: { title: string; description: string }[];
  englishBenefits?: { title: string; description: string }[];
  whyChooseUs?: { title: string; description: string }[];
  englishWhyChooseUs?: { title: string; description: string }[];
  typicalDocuments: string[];
  englishTypicalDocuments: string[];
  ctaHeadline?: string;
  englishCtaHeadline?: string;
  ctaSubtext?: string;
  englishCtaSubtext?: string;
  matrix: IndustryModuleMatrix;
  englishMatrix: IndustryModuleMatrix;
  iconName: 'Calculator' | 'Home' | 'Scale' | 'Users' | 'Landmark' | 'HeartPulse' | 'Hammer' | 'Car' | 'Briefcase' | 'Code';
}

export const INDUSTRIES_DATA: IndustryDetail[] = [
  {
    id: 'branche-revision-bogfoering',
    slug: 'revision-bogfoering',
    englishSlug: 'accounting-audit',
    number: 1,
    name: 'Revision & Bogføring',
    englishName: 'Accounting & Auditing',
    seoTitle: 'Revision & Bogføring – Digital Signatur & KYC | KvikSign',
    englishSeoTitle: 'Accounting & Audit – Digital Signatures & KYC | KvikSign',
    title: 'Digital signatur og automatiseret hvidvasktjek (KYC) for revisorer og bogholdere',
    englishTitle: 'Digital signatures and automated AML / KYC verification for accountants and auditors',
    shortDescription: 'Overhold Hvidvaskloven automatisk. Verificer reelle ejere med MitID via KvikID, indhent underskrifter på årsrapporter med PAdES LTV-certifikater, og modtag klientbilag sikkert.',
    englishShortDescription: 'Comply with AML regulations automatically. Verify beneficial owners via MitID with KvikID, collect signatures on annual reports with PAdES LTV certificates, and securely receive client vouchers.',
    metaDescription: 'Overhold Hvidvaskloven og spar 80% af tiden på kundeoptagelse og underskrift af årsrapporter med KvikSign og KvikID.',
    englishMetaDescription: 'Comply with AML regulations automatically and cut onboarding time with KvikSign electronic signatures and MitID KYC.',
    heroImage: 'https://firebasestorage.googleapis.com/v0/b/kviksign-db-europe/o/Website%2FPhotos%2FGeneric%20work%20environment.webp?alt=media&token=ea262c01-9ddd-49b9-bcb5-5b751aa0075f',
    heroImageAlt: 'Revisor der arbejder med regnskaber og digital signatur',
    englishHeroImageAlt: 'Auditor working with financial statements and digital signature',
    introText: 'Som revisor eller bogholder er du underlagt strenge lovkrav i Hvidvaskloven og GDPR. Samtidig bruger mange rådgivere uforholdsmæssigt meget tid på manuel indsamling af pas, sygesikringsbeviser, engagementbreve og underskrifter på årsrapporter. KvikSign forener lovpligtig compliance med lynhurtig administration.',
    englishIntroText: 'As an accountant or auditor, you are subject to strict legal requirements under AML legislation and GDPR. At the same time, many advisors spend excessive time manually collecting ID cards, engagement letters, and signatures on annual reports. KvikSign combines legal compliance with lightning-fast administration.',
    challengeSolutions: [
      {
        title: 'Tidskrævende hvidvaskkontrol (KYC)',
        challenge: 'Manuel indhentning af ID-kopier over usikker mail er både langsomt og i strid med GDPR.',
        solution: 'Send et link via SMS eller mail. Kunden verificerer sig med MitID på under 60 sekunder. Systemet validerer identiteten og genererer et revisionssikkert PDF-dossier til Hvidvaskloven.'
      },
      {
        title: 'Forsinkede underskrifter på årsrapporter',
        challenge: 'Travle ledelsesmedlemmer og bestyrelser er svære at samle til fysisk signering.',
        solution: 'Udsend årsrapporten til flerparts-signering med MitID. Dokumentet forsegles med PAdES LTV-certifikater, som er godkendt af Erhvervsstyrelsen (Virk.dk).'
      },
      {
        title: 'Usikker transmission af fortrolige regnskabsbilag',
        challenge: 'Fremsendelse af CPR-numre og skatteoplysninger på åben e-mail risikerer GDPR-bøder.',
        solution: 'Del og modtag fortrolige sagsakter krypteret med MitID-adgangskontrol via KvikSend.'
      }
    ],
    typicalDocuments: [
      'Årsrapporter, ledelsesberetninger og revisionspåtegninger',
      'Aftalebreve, rådgivningskontrakter og engagementer',
      'Fuldmagter til Skat og Erhvervsstyrelsen',
      'Databehandleraftaler (DPA)',
      'Lovpligtige KYC-dossierer og risikovurderinger'
    ],
    englishTypicalDocuments: [
      'Annual reports, management statements, and auditor reports',
      'Engagement letters, advisory contracts, and mandates',
      'Powers of attorney for Tax Authorities and Business Registries',
      'Data Processing Agreements (DPA)',
      'Statutory KYC dossiers and risk assessments'
    ],
    ctaHeadline: 'Få et 100% revisionssikkert workflow i dag',
    englishCtaHeadline: 'Get a 100% audit-proof workflow today',
    ctaSubtext: 'Prøv KvikSign og KvikID gratis med 5 credits – kom i gang på 2 minutter.',
    englishCtaSubtext: 'Try KvikSign and KvikID free with 5 credits – get started in 2 minutes.',
    matrix: {
      kvikSign: 'Årsregnskaber & aftalebreve',
      kvikID: 'Hvidvasktjek & reelle ejere',
      kvikSend: 'Sikker modtagelse af bilag',
      kvikForm: 'Kundeonboarding'
    },
    englishMatrix: {
      kvikSign: 'Annual reports & engagement letters',
      kvikID: 'AML checks & beneficial owners',
      kvikSend: 'Secure voucher intake',
      kvikForm: 'Client onboarding'
    },
    iconName: 'Calculator'
  },
  {
    id: 'branche-ejendomsmaeglere',
    slug: 'ejendomsmaeglere',
    englishSlug: 'real-estate',
    number: 2,
    name: 'Ejendomsmæglere & Boligadvokater',
    englishName: 'Real Estate & Property Law',
    seoTitle: 'Ejendomsmæglere – Digital Signatur & KYC-tjek | KvikSign',
    englishSeoTitle: 'Real Estate – Digital Signatures & KYC Check | KvikSign',
    title: 'Digital signatur, købsaftaler og lynhurtig KYC til ejendomsmæglere',
    englishTitle: 'Digital signatures, purchase agreements, and instant KYC for real estate agents',
    shortDescription: 'Luk købsaftaler på under 2 timer. Indhent MitID-verifikation på køber og sælger ved budgivning, og styr rækkefølgen af underskrivere automatisk.',
    englishShortDescription: 'Close purchase agreements in under 2 hours. Obtain MitID verification on buyers and sellers during bidding, and automate signer sequences.',
    metaDescription: 'Hurtig digital signatur på købsaftaler og automatiseret identitetsverifikation med MitID for ejendomsmæglere i Danmark.',
    englishMetaDescription: 'Fast digital signatures on purchase agreements and automated MitID identity verification for real estate professionals.',
    heroImage: 'https://firebasestorage.googleapis.com/v0/b/kviksign-db-europe/o/Website%2FPhotos%2FReal%20estate.webp?alt=media&token=b0fa2f1a-631e-4a4b-acb8-f2873c49a547',
    heroImageAlt: 'Moderne bolig og digital købsaftale',
    englishHeroImageAlt: 'Modern home and digital purchase agreement',
    introText: 'I bolighandler er momentum alt. Når en køber har lagt et bud, eller en sælger vil sætte boligen til salg, skal dokumenterne underskrives, før interessen kølner. Samtidig skal mæglerbutikken leve op til Hvidvasklovens strenge krav om identifikation af både køber og sælger.',
    englishIntroText: 'In real estate, momentum is everything. When a buyer places a bid or a seller lists a property, documents need signing before momentum cools. Meanwhile, agency offices must strictly meet statutory identification and AML standards.',
    benefits: [
      {
        title: 'Formidlingsaftalen underskrives hos sælger',
        description: 'Få sælgers MitID-underskrift direkte på mobilen eller tabletten under vurderingsmødet.'
      },
      {
        title: 'Automatisk KYC ved budgivning',
        description: 'Indtast købers CPR-nummer i KvikID; køber modtager straks en MitID-notifikation, swiper for at godkende, og identiteten er bekræftet.'
      },
      {
        title: 'Sekventiel signering af købsaftalen',
        description: 'Købere, sælgere og mægler underskriver automatisk i fastlagt rækkefølge.'
      }
    ],
    englishBenefits: [
      {
        title: 'Brokerage agreement signed on-site with the seller',
        description: 'Collect seller MitID signature directly on mobile or tablet during the valuation meeting.'
      },
      {
        title: 'Automated KYC at bidding time',
        description: 'Enter buyer’s CPR in KvikID; the buyer receives a MitID notification, swipes to approve, and identity is verified in seconds.'
      },
      {
        title: 'Sequential signing of the purchase contract',
        description: 'Buyers, sellers, and agents sign in strict predefined order without manual follow-up.'
      }
    ],
    typicalDocuments: [
      'Købsaftaler og tillæg',
      'Formidlingsaftaler og salgsbudgetter',
      'Deponeringserklæringer',
      'Overtagelsesprotokoller og refusionsopgørelser'
    ],
    englishTypicalDocuments: [
      'Purchase agreements and addenda',
      'Brokerage contracts and sales budgets',
      'Escrow and deposit statements',
      'Handover protocols and settlement accounts'
    ],
    ctaHeadline: 'Luk flere bolighandler med KvikSign',
    englishCtaHeadline: 'Close more property deals with KvikSign',
    ctaSubtext: 'Kom i gang gratis på under 2 minutter.',
    englishCtaSubtext: 'Get started for free in under 2 minutes.',
    matrix: {
      kvikSign: 'Købsaftaler & deponeringer',
      kvikID: 'Køber/sælger MitID-tjek',
      kvikSend: 'Salgs- og tilstandsrapporter',
      kvikForm: 'Kunderegistrering'
    },
    englishMatrix: {
      kvikSign: 'Purchase agreements & escrow',
      kvikID: 'Buyer/seller MitID check',
      kvikSend: 'Property condition reports',
      kvikForm: 'Customer registration'
    },
    iconName: 'Home'
  },
  {
    id: 'branche-advokater-jura',
    slug: 'advokater-jura',
    englishSlug: 'legal-law-firms',
    number: 3,
    name: 'Advokater & Juridisk Rådgivning',
    englishName: 'Lawyers & Legal Counsel',
    seoTitle: 'Advokater & Jura – Fortrolig Digital Signatur | KvikSign',
    englishSeoTitle: 'Legal & Law Firms – Secure Digital Signatures | KvikSign',
    title: 'Maksimal fortrolighed og uafviselig digital signatur til advokater',
    englishTitle: 'Maximum confidentiality and indisputable digital signatures for lawyers',
    shortDescription: 'Maksimal fortrolighed og uafviselig bevisværdi. Udveksl sagsakter krypteret via KvikSend, og få gyldige eIDAS-signaturer på forlig, ægtepagter og samarbejdsaftaler.',
    englishShortDescription: 'Maximum confidentiality and legal evidentiary value. Exchange case files encrypted via KvikSend, and get valid eIDAS signatures on settlements and contracts.',
    metaDescription: 'Maksimal fortrolighed og uafviselig digital signatur med MitID. Send klientsager krypteret med KvikSend og eIDAS PAdES.',
    englishMetaDescription: 'Confidential and legally binding digital signatures with MitID. Share sensitive client files securely via KvikSend.',
    heroImage: 'https://firebasestorage.googleapis.com/v0/b/kviksign-db-europe/o/Website%2FPhotos%2FGeneric%20work%20environment.webp?alt=media&token=ea262c01-9ddd-49b9-bcb5-5b751aa0075f',
    heroImageAlt: 'Juridiske dokumenter og advokatkontor',
    englishHeroImageAlt: 'Legal documents and law firm desk',
    introText: 'For advokater og jurister er tavshedspligt, GDPR-overholdelse og urokkelig bevisværdi i retten altafgørende. KvikSign sikrer at hver underskrift og fildeling overholder de højeste standarder for europæisk retsgyldighed.',
    englishIntroText: 'For attorneys and legal advisors, confidentiality, GDPR compliance, and indisputable court admissibility are critical. KvikSign ensures that every signature and file delivery fulfills the highest European legal standards.',
    whyChooseUs: [
      {
        title: 'eIDAS PAdES LTV Forsegling',
        description: 'Dokumenterne tidsstemples og krypteres, så de har fuld retsgyldighed i alle EU-lande.'
      },
      {
        title: 'Udtømmende Revisionslog (Audit Trail)',
        description: 'Logger IP-adresser, tidspunkter og MitID UUID for hver handling.'
      },
      {
        title: 'KvikSend Klientboks',
        description: 'Udveksl følsomme procesakter og beviser med klienter via MitID-låste links.'
      }
    ],
    englishWhyChooseUs: [
      {
        title: 'eIDAS PAdES LTV Sealing',
        description: 'Documents are timestamped and cryptographically sealed with permanent legal validity across all EU member states.'
      },
      {
        title: 'Exhaustive Audit Trail',
        description: 'Logs IP addresses, exact timestamps, and MitID UUID signatures for every action.'
      },
      {
        title: 'KvikSend Client Box',
        description: 'Exchange confidential litigation files and evidence with clients using MitID-locked links.'
      }
    ],
    typicalDocuments: [
      'B2B Samarbejdsaftaler og M&A-kontrakter',
      'Fuldmagter, ægtepagter og arveaftaler',
      'Forligs- og fratrædelsesaftaler',
      'Databehandleraftaler (DPA) og NDA\'er'
    ],
    englishTypicalDocuments: [
      'B2B Collaboration Agreements and M&A Contracts',
      'Powers of attorney, prenuptials, and inheritance deeds',
      'Settlement and severance agreements',
      'Data Processing Agreements (DPA) and NDAs'
    ],
    ctaHeadline: 'Styrk advokatkontorets digitale sikkerhed i dag',
    englishCtaHeadline: 'Strengthen your law firm’s digital security today',
    ctaSubtext: 'Få 5 gratis test-credits og oplev eIDAS PAdES-forseglingen med det samme.',
    englishCtaSubtext: 'Get 5 free test credits and experience eIDAS PAdES sealing immediately.',
    matrix: {
      kvikSign: 'Forlig, testamenter & DPA',
      kvikID: 'Klient- & fuldmagtstjek',
      kvikSend: 'Fortrolige procesakter',
      kvikForm: 'Samtykke- og sagsblanketter'
    },
    englishMatrix: {
      kvikSign: 'Settlements, wills & DPAs',
      kvikID: 'Client & power of attorney checks',
      kvikSend: 'Confidential litigation files',
      kvikForm: 'Consent & case intake forms'
    },
    iconName: 'Scale'
  },
  {
    id: 'branche-hr-rekruttering',
    slug: 'hr-rekruttering',
    englishSlug: 'hr-recruitment',
    number: 4,
    name: 'HR, Rekruttering & Vikarbureauer',
    englishName: 'HR, Recruitment & Staffing',
    seoTitle: 'HR & Rekruttering: Gør HR-processer enklere | KvikSign',
    englishSeoTitle: 'HR & Recruitment: Simplify HR Workflows | KvikSign',
    title: 'Gør jeres HR-processer og ansættelser enklere',
    englishTitle: 'Make your HR processes simpler & faster',
    shortDescription: 'Få ansættelseskontrakten underskrevet på mobilen samme dag. Indsaml CPR, bankoplysninger og samtykkeerklæringer digitalt via KvikForm.',
    englishShortDescription: 'Get employment contracts signed on mobile the very same day. Collect civil registry numbers, bank details, and consent digitally via KvikForm.',
    metaDescription: 'Gør jeres HR-processer enklere med KvikSign. Send ansættelseskontrakter til MitID-signering, saml medarbejderoplysninger og automatiser onboarding.',
    englishMetaDescription: 'Simplify your HR processes with KvikSign. Send employment contracts for MitID signing, gather employee data, and automate onboarding seamlessly.',
    heroImage: 'https://firebasestorage.googleapis.com/v0/b/kviksign-db-europe/o/Website%2FPhotos%2FHR%20and%20people.webp?alt=media&token=8e5db8f7-f4fc-4ee9-8e61-4a66b993f2e7',
    heroImageAlt: 'Smilende kvindelig HR-konsulent med briller på moderne kontor',
    englishHeroImageAlt: 'Smiling female HR professional with glasses working in modern Scandinavian office',
    introText: 'Gør administrationen lettere for både HR og medarbejderen. Med KvikSign kan ansættelseskontrakter, tillæg og samtykker udsendes, godkendes og underskrives med MitID i ét samlet, automatiseret forløb.',
    englishIntroText: 'Streamline administrative workflows for HR and new hires alike. With KvikSign, employment agreements, contract amendments, and consents are dispatched, approved, and digitally signed with MitID in a single seamless flow.',
    benefits: [
      {
        title: '85% underskrives inden for 2 timer',
        description: 'Kandidaten bipper godkendelsen i MitID på mobilen på få sekunder.'
      },
      {
        title: 'KvikForm Onboarding',
        description: 'Indsaml CPR, bankkonto, IT-ønsker og pårørende digitalt uden tastefejl.'
      },
      {
        title: 'Overholdelse af Ansættelsesbevisloven',
        description: 'Sikr rettidig skriftlig udlevering af vilkår med fuldt kryptografisk tidsstempel.'
      }
    ],
    englishBenefits: [
      {
        title: '85% signed within 2 hours',
        description: 'The candidate approves with MitID on their smartphone in seconds.'
      },
      {
        title: 'KvikForm Onboarding',
        description: 'Collect ID data, bank accounts, hardware preferences, and emergency contacts cleanly without manual typing errors.'
      },
      {
        title: 'Compliance with Employment Terms Legislation',
        description: 'Ensure timely written delivery of terms with indisputable cryptographic timestamps.'
      }
    ],
    typicalDocuments: [
      'Ansættelseskontrakter og funktionæraftaler',
      'Tavshedserklæringer (NDA)',
      'IT- og sikkerhedspolitikker',
      'Fratrædelsesaftaler'
    ],
    englishTypicalDocuments: [
      'Employment agreements and salaried contracts',
      'Non-disclosure agreements (NDA)',
      'IT and security policy acceptances',
      'Severance and departure agreements'
    ],
    ctaHeadline: 'Gør din rekruttering lynhurtig',
    englishCtaHeadline: 'Make your recruitment lightning-fast',
    ctaSubtext: 'Prøv KvikSign gratis med 5 credits – ingen binding eller kreditkort påkrævet.',
    englishCtaSubtext: 'Try KvikSign free with 5 credits – no commitment or credit card required.',
    matrix: {
      kvikSign: 'Ansættelseskontrakter & tillæg',
      kvikID: 'Bekræftelse af kandidat-ID',
      kvikSend: 'Lønsedler & personalehåndbog',
      kvikForm: 'Stamdata & pårørendeformular'
    },
    englishMatrix: {
      kvikSign: 'Employment contracts & addenda',
      kvikID: 'Candidate identity verification',
      kvikSend: 'Payslips & staff handbook',
      kvikForm: 'Master data & contact form'
    },
    iconName: 'Users'
  },
  {
    id: 'branche-finans-forsikring',
    slug: 'finans-forsikring',
    englishSlug: 'finance-insurance',
    number: 5,
    name: 'Finans, Lån & Forsikring',
    englishName: 'Finance, Lending & Insurance',
    seoTitle: 'Finans & Forsikring – Digital Signatur & KvikID | KvikSign',
    englishSeoTitle: 'Finance & Insurance – Digital Signature & eID | KvikSign',
    title: 'Telefonisk MitID-verifikation og hurtige låneaftaler til finanssektoren',
    englishTitle: 'Phone MitID verification and fast loan agreements for the finance sector',
    shortDescription: 'Forebyg telefonsvindel og spoofing. Bekræft kundens identitet over telefonen på 10 sekunder med KvikID, og udsend låne- og kautionsaftaler til digital signatur.',
    englishShortDescription: 'Prevent phone fraud and spoofing. Confirm customer identity over the phone in 10 seconds with KvikID, and issue loan & guarantee agreements.',
    metaDescription: 'Luk låneaftaler og policer hurtigt med MitID-signering og forebyg telefonsvindel med live KvikID-verifikation i realtid.',
    englishMetaDescription: 'Close loan agreements and insurance policies with MitID signatures while preventing phone fraud with real-time KvikID.',
    heroImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80',
    heroImageAlt: 'Finansiel rådgivning og digital aftaleindgåelse',
    englishHeroImageAlt: 'Financial advisory and digital agreement workflow',
    introText: 'Finansielle virksomheder, långivere og forsikringsmæglere kæmper dagligt med to store udfordringer: sofistikeret telefonsvindel og langsom dokumentation ved udbetalinger. KvikID og KvikSign løser begge dele på få sekunder.',
    englishIntroText: 'Financial companies, lenders, and insurance brokers grapple with two key challenges: sophisticated phone spoofing and delayed credit documentation. KvikID and KvikSign solve both instantaneously.',
    benefits: [
      {
        title: '1. Live identitetsbekræftelse over telefonen',
        description: 'Når en kunde ringer ind med anmodning om kontoændringer eller låneoptagelse, sender rådgiveren en MitID push-besked via KvikID, som kunden godkender på 10 sekunder under opkaldet.'
      },
      {
        title: '2. Hurtig signering af kreditaftaler',
        description: 'Send lånedokumenter, kautionserklæringer og samtykker til SKAT til øjeblikkelig MitID-signering med uafviselig bevisværdi.'
      }
    ],
    englishBenefits: [
      {
        title: '1. Live identity confirmation during phone calls',
        description: 'When a customer calls requesting account changes or loan disbursement, the advisor triggers a MitID push via KvikID which the caller confirms in 10 seconds while on the line.'
      },
      {
        title: '2. Instant execution of credit agreements',
        description: 'Send loan agreements, guarantee deeds, and tax consent forms for instant MitID signature with unchallengeable evidentiary standing.'
      }
    ],
    typicalDocuments: [
      'Låne- og kreditaftaler',
      'Kautionserklæringer',
      'Samtykke til kreditoplysning (RKI / e-Skat)',
      'Skadesanmeldelser'
    ],
    englishTypicalDocuments: [
      'Loan and credit agreements',
      'Surety and guarantee declarations',
      'Consent for credit check & tax records',
      'Claims and damage notices'
    ],
    ctaHeadline: 'Sikr dine finansielle transaktioner',
    englishCtaHeadline: 'Secure your financial transactions',
    ctaSubtext: 'Kom i gang på 2 minutter med 5 gratis credits.',
    englishCtaSubtext: 'Get started in 2 minutes with 5 free credits.',
    matrix: {
      kvikSign: 'Låneaftaler & kautionserklæringer',
      kvikID: 'Telefonisk ID-tjek under opkald',
      kvikSend: 'Fortrolige kreditrapporter',
      kvikForm: 'Digitale låneansøgninger'
    },
    englishMatrix: {
      kvikSign: 'Loan agreements & guarantees',
      kvikID: 'Live telephone ID verification',
      kvikSend: 'Confidential credit reports',
      kvikForm: 'Digital loan applications'
    },
    iconName: 'Landmark'
  },
  {
    id: 'branche-sundhed-behandlere',
    slug: 'sundhed-behandlere',
    englishSlug: 'healthcare-clinics',
    number: 6,
    name: 'Sundhed, Psykologer & Behandlere',
    englishName: 'Healthcare, Clinics & Therapists',
    seoTitle: 'Sundhed & Klinikker – GDPR-sikker Signering | KvikSign',
    englishSeoTitle: 'Healthcare & Clinics – GDPR-secure Signatures | KvikSign',
    title: 'GDPR-sikker journaldeling og digitale samtykkeerklæringer',
    englishTitle: 'GDPR-secure medical record sharing and digital consent forms',
    shortDescription: 'Fuld beskyttelse af følsomme helbredsoplysninger (GDPR Artikel 9). Lad patienter udfylde anamnese online, og del journaler krypteret med MitID-oplåsning.',
    englishShortDescription: 'Full protection of sensitive health data (GDPR Article 9). Have patients complete anamnesis online, and share records encrypted with MitID access.',
    metaDescription: 'Beskyt følsomme helbredsoplysninger efter GDPR Artikel 9. Send journaler med MitID-oplåsning og indhent digitale samtykker.',
    englishMetaDescription: 'Protect sensitive health data in compliance with GDPR Article 9. Share records with MitID decryption and collect consents.',
    heroImage: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1200&q=80',
    heroImageAlt: 'Sundhedsfaglig behandler med tablet i klinik',
    englishHeroImageAlt: 'Healthcare clinician using tablet in clinic',
    introText: 'Behandlere, speciallæger og psykologer håndterer dagligt særligt følsomme personoplysninger (GDPR Artikel 9). KvikSign sikrer at samtykkeerklæringer og journaldeling altid sker 100% krypteret og lovmedholdeligt.',
    englishIntroText: 'Therapists, medical specialists, and psychologists manage sensitive health data daily (GDPR Article 9). KvikSign ensures that consent declarations and record deliveries remain strictly encrypted and compliant.',
    benefits: [
      {
        title: 'KvikForm Forhåndsregistrering',
        description: 'Patienter udfylder helbredsoplysninger, medicinliste og samtykke online før første konsultation.'
      },
      {
        title: 'KvikSend Sikker Journalboks',
        description: 'Del journaler, epikriser og testresultater krypteret med patientens personlige MitID-lås.'
      },
      {
        title: '100% GDPR & Sundhedslov-compliance',
        description: 'Fuld databehandleraftale (DPA), revisionslog og hosting i sikrede EU-datacentre.'
      }
    ],
    englishBenefits: [
      {
        title: 'KvikForm Pre-Intake',
        description: 'Patients fill out medical history, medication lists, and consent digitally before their first appointment.'
      },
      {
        title: 'KvikSend Medical File Box',
        description: 'Share patient records, discharge summaries, and test results securely locked with personal MitID access.'
      },
      {
        title: '100% GDPR & Health Legislation Compliance',
        description: 'Includes Data Processing Agreement (DPA), audit logs, and hosting in certified EU facilities.'
      }
    ],
    typicalDocuments: [
      'Patientjournaler og epikriser',
      'Samtykke til videregivelse af helbredsdata',
      'Helbredserklæringer og anamneseskemaer',
      'Behandlings- og honoraraftaler'
    ],
    englishTypicalDocuments: [
      'Patient records and clinical summaries',
      'Consent to medical data transmission',
      'Health declarations and anamnesis forms',
      'Treatment plans and fee agreements'
    ],
    ctaHeadline: 'Beskyt dine patientdata i dag',
    englishCtaHeadline: 'Protect your patient data today',
    ctaSubtext: 'Prøv KvikSend og KvikForm gratis med 5 credits.',
    englishCtaSubtext: 'Try KvikSend and KvikForm free with 5 credits.',
    matrix: {
      kvikSign: 'Behandlingsaftaler & samtykke',
      kvikID: 'Patientidentifikation',
      kvikSend: 'Journaler & epikriser (GDPR)',
      kvikForm: 'Helbreds- og indtagelsesskemaer'
    },
    englishMatrix: {
      kvikSign: 'Treatment contracts & consent',
      kvikID: 'Patient identification',
      kvikSend: 'Records & clinical reports (GDPR)',
      kvikForm: 'Health intake questionnaires'
    },
    iconName: 'HeartPulse'
  },
  {
    id: 'branche-haandvaerk-byggeri',
    slug: 'haandvaerk-byggeri',
    englishSlug: 'construction-contractors',
    number: 7,
    name: 'Håndværk, Byggeri & Entreprise',
    englishName: 'Trades, Construction & Contractors',
    seoTitle: 'Håndværk & Byggeri – Digital Signatur på Tilbud | KvikSign',
    englishSeoTitle: 'Construction & Trades – Mobile Signatures | KvikSign',
    title: 'Digital signatur på tilbud og ekstraarbejde direkte på byggepladsen',
    englishTitle: 'Digital signatures on quotes and extra work orders right on the job site',
    shortDescription: 'Undgå uenigheder om ekstraregninger. Få bygherrens underskrift på tilbud og ekstraarbejde direkte på byggepladsen via smartphone eller tablet.',
    englishShortDescription: 'Avoid disputes over extra work. Get client signatures on quotes and variations directly on the job site via smartphone or tablet.',
    metaDescription: 'Få bygherrens og kundens bindende underskrift på tilbud og ekstraarbejde direkte på byggepladsen via mobil og MitID.',
    englishMetaDescription: 'Secure binding client signatures on quotes and extra work orders on the job site directly via mobile and secure eID.',
    heroImage: 'https://firebasestorage.googleapis.com/v0/b/kviksign-db-europe/o/Website%2FPhotos%2FHandyman.webp?alt=media&token=8199e66b-f6f5-4fe9-b3ef-6201ee298da6',
    heroImageAlt: 'Håndværker og byggeleder med tablet på byggepladsen',
    englishHeroImageAlt: 'Tradesperson and project manager with tablet on construction site',
    introText: 'I byggebranchen går mange penge tabt, fordi aftaler om ekstraarbejde indgås mundtligt på byggepladsen uden skriftlig dokumentation. Med KvikSign og KvikForm får mester og byggeleder kundens bindende underskrift på få sekunder direkte på mobilen.',
    englishIntroText: 'In construction, significant revenue is lost because extra work orders are made verbally on site without written confirmation. With KvikSign and KvikForm, contractors secure legally binding sign-offs on mobile in seconds.',
    benefits: [
      {
        title: 'Mester får underskrift i marken',
        description: 'Opret en aftaleseddel i KvikForm og få kundens godkendelse med fingeren (touch) eller MitID med det samme.'
      },
      {
        title: 'Luk tilbud hurtigere',
        description: 'Send tilbud som link via SMS. Kunden godkender på farten uden at skulle printe eller scanne.'
      },
      {
        title: 'Uomtvistelig dokumentation',
        description: 'Revisionslog med fotobilag og tidsstempel fjerner alle konflikter ved slutafregning.'
      }
    ],
    englishBenefits: [
      {
        title: 'Signatures collected on the job site',
        description: 'Generate an extra work order via KvikForm and get instant client approval via touchscreen drawing or MitID.'
      },
      {
        title: 'Close estimates faster',
        description: 'Send quotes directly via SMS link. Clients approve immediately on the go without printing.'
      },
      {
        title: 'Uncontested documentation',
        description: 'Audit logs with photo attachments and timestamps eliminate all disputes during final billing.'
      }
    ],
    typicalDocuments: [
      'Aftalesedler om ekstraarbejde (AB 18 / AB Forbruger)',
      'Tilbud og ordrebekræftelser',
      'Entreprisekontrakter',
      'Afleveringsprotokoller og mangellister'
    ],
    englishTypicalDocuments: [
      'Variation and extra work order slips',
      'Quotations and order confirmations',
      'Subcontractor and general contractor agreements',
      'Handover protocols and punch lists'
    ],
    ctaHeadline: 'Få betaling for alt udført arbejde',
    englishCtaHeadline: 'Ensure payment for all completed work',
    ctaSubtext: 'Prøv KvikSign gratis på mobilen i dag.',
    englishCtaSubtext: 'Try KvikSign free on mobile today.',
    matrix: {
      kvikSign: 'Tilbudsgodkendelse & AB 18',
      kvikID: 'ID-kontrol af underentreprenør',
      kvikSend: 'Store tegninger & beregninger',
      kvikForm: 'Aftalesedler om ekstraarbejde'
    },
    englishMatrix: {
      kvikSign: 'Quote acceptance & standard contracts',
      kvikID: 'Subcontractor ID verification',
      kvikSend: 'CAD drawings & structural calculations',
      kvikForm: 'On-site variation order sheets'
    },
    iconName: 'Hammer'
  },
  {
    id: 'branche-bilforhandlere-leasing',
    slug: 'bilforhandlere-leasing',
    englishSlug: 'automotive-dealerships',
    number: 8,
    name: 'Bilforhandlere & Leasing',
    englishName: 'Automotive Dealerships & Leasing',
    seoTitle: 'Bilforhandlere – Digitale Slutsedler & MitID | KvikSign',
    englishSeoTitle: 'Automotive & Leasing – Digital Sales Contracts | KvikSign',
    title: 'Digitale slutsedler, leasingaftaler og prøvekørsler med kørekorttjek',
    englishTitle: 'Digital sales contracts, leasing agreements, and test-drive license checks',
    shortDescription: 'Verificer kørekort og identitet med MitID på 30 sekunder før prøvekørsler, og underskriv slutsedler og leasingaftaler direkte på salgschefens tablet.',
    englishShortDescription: 'Verify driver’s licenses and identity via MitID in 30 seconds before test drives, and sign vehicle contracts directly on tablets.',
    metaDescription: 'Underskriv slutsedler og leasingaftaler digitalt på iPad. Forebyg identitetstyveri ved prøvekørsler med hurtigt MitID-tjek.',
    englishMetaDescription: 'Sign purchase and leasing contracts digitally in the showroom. Prevent test-drive identity theft with instant MitID checks.',
    heroImage: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&w=1200&q=80',
    heroImageAlt: 'Bilsælger og kunde der underskriver aftale på tablet',
    englishHeroImageAlt: 'Car sales consultant and customer signing contract on tablet',
    introText: 'Bilforhandlere og leasingselskaber skal balancere hurtig kundebetjening i udstillingen med streng identitetskontrol før prøvekørsler og bindende slutsedler. KvikSign og KvikID samler hele bilsalget i én smidig digital proces.',
    englishIntroText: 'Automotive dealerships and leasing companies must balance quick showroom service with strict ID verification before test drives and vehicle handovers. KvikSign and KvikID digitize the entire sales process seamlessly.',
    benefits: [
      {
        title: '1. Prøvekørselstjek på 30 sekunder',
        description: 'Kunden bipper MitID og uploader kørekort via KvikID før udlevering af bilnøgler.'
      },
      {
        title: '2. Slutseddel underskrives i salgslokalet',
        description: 'Kunden signerer digitalt på sælgerens iPad – juridisk bindende med det samme.'
      },
      {
        title: '3. Flerpartsleasing',
        description: 'Automatisk flow mellem køber, medansøger og finansieringsselskab uden forsinkende papirarbejde.'
      }
    ],
    englishBenefits: [
      {
        title: '1. Test-drive verification in 30 seconds',
        description: 'The driver confirms via MitID and uploads their driver’s license via KvikID before keys are handed over.'
      },
      {
        title: '2. Sales contract executed in the showroom',
        description: 'Customers sign digitally on the consultant’s iPad – instantly and legally binding.'
      },
      {
        title: '3. Multi-party leasing workflows',
        description: 'Automated sequential signing between buyer, co-applicant, and leasing provider.'
      }
    ],
    typicalDocuments: [
      'Digitale slutsedler for nye og brugte biler',
      'Privat- og erhvervsleasingaftaler',
      'Prøvekørselsaftaler med ansvarsfraskrivelse',
      'Lånebilsaftaler'
    ],
    englishTypicalDocuments: [
      'Digital sales contracts for new and used vehicles',
      'Personal and commercial leasing agreements',
      'Test-drive agreements with liability waivers',
      'Courtesy vehicle agreements'
    ],
    ctaHeadline: 'Sæt fart på bilsalget i salgslokalet',
    englishCtaHeadline: 'Accelerate showroom automotive sales',
    ctaSubtext: 'Start gratis med 5 credits – kom i gang på 2 minutter.',
    englishCtaSubtext: 'Start free with 5 credits – get started in 2 minutes.',
    matrix: {
      kvikSign: 'Slutsedler & leasingaftaler',
      kvikID: 'Kørekorttjek ved prøvekørsel',
      kvikSend: 'Overtagelses- & skadesrapporter',
      kvikForm: 'Lånebilsaftaler'
    },
    englishMatrix: {
      kvikSign: 'Sales contracts & leasing terms',
      kvikID: 'Driver’s license check for test drives',
      kvikSend: 'Handover & damage inspection reports',
      kvikForm: 'Courtesy vehicle intake forms'
    },
    iconName: 'Car'
  },
  {
    id: 'branche-bestyrelser-foreninger',
    slug: 'bestyrelser-foreninger',
    englishSlug: 'boards-associations',
    number: 9,
    name: 'Bestyrelser & Foreninger',
    englishName: 'Boards, Associations & Housing',
    seoTitle: 'Bestyrelser & Foreninger – Digital Signatur | KvikSign',
    englishSeoTitle: 'Boards & Associations – Digital Board Signatures | KvikSign',
    title: 'Papirfrit bestyrelsesarbejde: Underskriv referater og vedtægter med MitID',
    englishTitle: 'Paperless board governance: Sign minutes and bylaws with MitID',
    shortDescription: 'Få alle bestyrelsesmedlemmers underskrifter på referater, vedtægtsændringer og årsrapporter på få timer uden papirbunker.',
    englishShortDescription: 'Collect all board members’ signatures on meeting minutes, bylaws, and annual reports in hours without paper clutter.',
    metaDescription: 'Papirfrit bestyrelsesarbejde: Få alle bestyrelsesmedlemmers underskrift på mødereferater og årsregnskaber på under 24 timer.',
    englishMetaDescription: 'Paperless board governance: Collect all board member signatures on meeting minutes and financial statements within 24 hours.',
    heroImage: 'https://firebasestorage.googleapis.com/v0/b/kviksign-db-europe/o/Website%2FPhotos%2FGeneric%20work%20environment.webp?alt=media&token=ea262c01-9ddd-49b9-bcb5-5b751aa0075f',
    heroImageAlt: 'Professionel bestyrelse i mødelokale',
    englishHeroImageAlt: 'Professional corporate board in conference room',
    introText: 'Bestyrelsesarbejde i virksomheder, boligselskaber og foreninger forsinkes ofte af, at referater og protokoller skal printes og cirkuleres manuelt blandt medlemmer spredt over hele landet. KvikSign gør hele processen digital, automatisk og 100% gyldig til Erhvervsstyrelsen.',
    englishIntroText: 'Governance in corporations, housing associations, and NGOs is frequently delayed by printing and physically circulating meeting minutes among members across the country. KvikSign makes the entire workflow digital, automatic, and fully approved for business registries.',
    benefits: [
      {
        title: 'Flerparts-signering',
        description: 'Udsend referatet til 5-7 medlemmer på én gang eller i fast rækkefølge.'
      },
      {
        title: 'Godkendt til Erhvervsstyrelsen',
        description: 'PAdES LTV-certifikater opfylder alle officielle krav på Virk.dk.'
      },
      {
        title: 'Automatiske rykkere',
        description: 'Platformen minder automatisk manglende medlemmer om at signere, så du slipper for at følge op manuelt.'
      }
    ],
    englishBenefits: [
      {
        title: 'Multi-party signing',
        description: 'Distribute minutes to 5-10 board members simultaneously or in strict predefined sequence.'
      },
      {
        title: 'Official Business Authority Approval',
        description: 'PAdES LTV cryptoseals satisfy all formal filing criteria on Virk.dk and national registries.'
      },
      {
        title: 'Automated Reminders',
        description: 'The platform automatically reminds pending signers so administrators never have to chase follow-ups manually.'
      }
    ],
    typicalDocuments: [
      'Bestyrelsesreferater og protokoller',
      'Generalforsamlingsreferater og dirigentprotokollater',
      'Vedtægtsændringer',
      'Tegnings- og bankfuldmagter'
    ],
    englishTypicalDocuments: [
      'Board minutes and official resolutions',
      'General assembly minutes and chairman protocols',
      'Bylaw amendments and articles of association',
      'Signing authorizations and bank mandates'
    ],
    ctaHeadline: 'Forenkl jeres bestyrelsesarbejde',
    englishCtaHeadline: 'Simplify your board governance',
    ctaSubtext: 'Opret en gratis konto og send jeres første referat til MitID-signering i dag.',
    englishCtaSubtext: 'Create a free account and send your first board resolution for MitID signature today.',
    matrix: {
      kvikSign: 'Referater & generalforsamling',
      kvikID: 'Dirigent- & formandsvalidering',
      kvikSend: 'Strategipapirer & regnskaber',
      kvikForm: 'Fuldmagter til generalforsamling'
    },
    englishMatrix: {
      kvikSign: 'Minutes & AGM resolutions',
      kvikID: 'Chairman validation & proxy checks',
      kvikSend: 'Strategy decks & confidential accounts',
      kvikForm: 'General assembly proxy voting forms'
    },
    iconName: 'Briefcase'
  },
  {
    id: 'branche-konsulenter-it',
    slug: 'konsulenter-it',
    englishSlug: 'consultants-it-agencies',
    number: 10,
    name: 'Konsulenter, IT & Bureauer',
    englishName: 'Consultants, IT & Agencies',
    seoTitle: 'Konsulenter & IT – B2B-aftaler & NDA Signering | KvikSign',
    englishSeoTitle: 'Consultants & IT – B2B Agreements & NDAs | KvikSign',
    title: 'Standardiserede B2B-aftaler, DPA og NDA til konsulenter og bureauer',
    englishTitle: 'Standardized B2B agreements, DPAs, and NDAs for consultants and agencies',
    shortDescription: 'Gør din rådgivning skalerbar. Udsend B2B-aftaler, SLA\'er, databehandleraftaler (DPA) og NDA\'er med faste skabeloner med ét klik.',
    englishShortDescription: 'Scale your consulting business. Issue B2B contracts, SLAs, DPAs, and NDAs from standardized templates with a single click.',
    metaDescription: 'Gør din konsulentforretning skalerbar. Udsend professionelle samarbejdsaftaler, SLA\'er og NDA\'er med genanvendelige skabeloner.',
    englishMetaDescription: 'Scale your consulting practice. Send client service agreements, SLAs, and NDAs with reusable templates and MitID signature.',
    heroImage: 'https://firebasestorage.googleapis.com/v0/b/kviksign-db-europe/o/Website%2FPhotos%2FCasual%20work%20environment.webp?alt=media&token=f32203e2-8787-45ed-a53a-547f30452c8e',
    heroImageAlt: 'Konsulenter der gennemgår digital strategi på bærbar computer',
    englishHeroImageAlt: 'Consultants reviewing digital strategy on laptop',
    introText: 'For konsulenthuse, IT-virksomheder og digitale bureauer er hurtig aftaleindgåelse afgørende for at starte projekter til tiden. Med KvikSigns skabelonmotor og hvidmærkede modtageroplevelse fremstår jeres bureau 100% professionelt.',
    englishIntroText: 'For consulting firms, IT companies, and digital agencies, swift agreement closing is vital to launching client projects on schedule. With KvikSign templates and white-labeled signing interfaces, your brand looks 100% professional.',
    benefits: [
      {
        title: 'Skabeloner med 1 klik',
        description: 'Udsend standardiserede konsulentaftaler, opdragsbeskrivelser og SLA\'er på sekunder.'
      },
      {
        title: 'Hvidmærket modtageroplevelse',
        description: 'Dit eget logo og brandfarver på e-mails og signeringssider for et gennemført professionelt udtryk.'
      },
      {
        title: 'KvikSend & KvikForm',
        description: 'Indsaml onboarding-krav fra kunden og del API-nøgler, kode og adgange krypteret.'
      }
    ],
    englishBenefits: [
      {
        title: '1-click reusable templates',
        description: 'Dispatch standardized consulting agreements, statements of work, and SLAs in seconds.'
      },
      {
        title: 'White-labeled signer experience',
        description: 'Your own custom logo and branding across emails and signing rooms for a seamless brand image.'
      },
      {
        title: 'KvikSend & KvikForm integration',
        description: 'Collect client project briefings and deliver API keys, repositories, and credentials securely encrypted.'
      }
    ],
    typicalDocuments: [
      'B2B Samarbejdsaftaler og Retainers',
      'Databehandleraftaler (DPA)',
      'Service Level Agreements (SLA)',
      'Fortrolighedsaftaler (NDA)'
    ],
    englishTypicalDocuments: [
      'B2B Master Service Agreements and Retainers',
      'Data Processing Agreements (DPA)',
      'Service Level Agreements (SLA)',
      'Non-Disclosure Agreements (NDA)'
    ],
    ctaHeadline: 'Luk flere konsulentaftaler hurtigere',
    englishCtaHeadline: 'Close more consulting retainers faster',
    ctaSubtext: 'Prøv KvikSign gratis med 5 credits – kom i gang på 2 minutter.',
    englishCtaSubtext: 'Try KvikSign free with 5 credits – get started in 2 minutes.',
    matrix: {
      kvikSign: 'Samarbejdsaftaler, DPA & NDA',
      kvikID: 'Kundevalidering',
      kvikSend: 'Kildekoder, nøgler & adgange',
      kvikForm: 'Briefing & opstartsskemaer'
    },
    englishMatrix: {
      kvikSign: 'Service agreements, DPA & NDA',
      kvikID: 'Client identity validation',
      kvikSend: 'Source code, API keys & credentials',
      kvikForm: 'Briefing & project kick-off forms'
    },
    iconName: 'Code'
  }
];

const SWEDISH_SLUG_ALIASES: Record<string, string> = {
  'redovisning-revision': 'revision-bogfoering',
  'fastighetsmaklare': 'ejendomsmaeglere',
  'advokater-juridik': 'advokater-jura',
  'hr-rekrytering': 'hr-rekruttering',
  'finans-forsakring': 'finans-forsikring',
  'vard-behandlare': 'sundhed-behandlere',
  'bygg-hantverk': 'haandvaerk-byggeri',
  'bilhandlare-leasing': 'bilforhandlere-leasing',
  'styrelser-foreningar': 'bestyrelser-foreninger',
  'konsulter-it': 'konsulenter-it'
};

export function getIndustryBySlug(slug: string, lang: 'da' | 'en' | 'sv' = 'da'): IndustryDetail | undefined {
  const cleanSlug = slug.toLowerCase().replace(/^\/+|\/+$/g, '').split('?')[0].split('#')[0];
  const mappedSlug = SWEDISH_SLUG_ALIASES[cleanSlug] || cleanSlug;
  return INDUSTRIES_DATA.find(
    (item) => item.slug.toLowerCase() === mappedSlug || item.englishSlug.toLowerCase() === mappedSlug
  );
}

export const SWEDISH_INDUSTRIES_MAP: Record<string, {
  name: string;
  title: string;
  shortDescription: string;
  seoTitle: string;
  metaDescription: string;
  heroImageAlt: string;
  typicalDocuments: string[];
  ctaHeadline: string;
  ctaSubtext: string;
  matrix: IndustryModuleMatrix;
}> = {
  'revision-bogfoering': {
    name: 'Revision & Bokföring',
    title: 'Digital signatur och automatiserad penningtvättskontroll (KYC) för revisorer och redovisningskonsulter',
    shortDescription: 'Följ penningtvättslagen automatiskt. Verifiera verkliga huvudmän med BankID via KvikID, samla in underskrifter på årsredovisningar med PAdES LTV-certifikat och ta emot klientunderlag säkert.',
    seoTitle: 'Revision & Bokföring – Digital signatur & KYC | KvikSign',
    metaDescription: 'Efterlev penningtvättslagen och spara 80% av tiden på kundonboarding och signering av årsredovisningar med KvikSign och KvikID.',
    heroImageAlt: 'Revisor som arbetar med årsbokslut och digital signatur',
    typicalDocuments: [
      'Årsredovisningar och revisionsberättelser',
      'Uppdragsbrev och rådgivningsavtal',
      'Fullmakter till Skatteverket och Bolagsverket',
      'Personuppgiftsbiträdesavtal (PUB)',
      'Lagstadgade KYC-dossierer och riskbedömningar'
    ],
    ctaHeadline: 'Få ett 100% revisionssäkert arbetsflöde idag',
    ctaSubtext: 'Prova KvikSign och KvikID gratis med 5 credits – kom igång på 2 minuter.',
    matrix: {
      kvikSign: 'Årsredovisningar & uppdragsbrev',
      kvikID: 'Penningtvättskontroll & verkliga huvudmän',
      kvikSend: 'Säker mottagning av verifikationer',
      kvikForm: 'Klientonboarding'
    }
  },
  'ejendomsmaeglere': {
    name: 'Fastighetsmäklare & Bostadsjuridik',
    title: 'Digital signatur, köpekontrakt och snabb KYC för fastighetsmäklare',
    shortDescription: 'Stäng köpekontrakt på under 2 timmar. Hämta BankID-verifiering på köpare och säljare vid budgivning och styr signaturordningen automatiskt.',
    seoTitle: 'Fastighetsmäklare – Digital signatur & KYC-kontroll | KvikSign',
    metaDescription: 'Snabb digital signatur på köpekontrakt och automatiserad identitetsverifiering med BankID för fastighetsmäklare.',
    heroImageAlt: 'Modernt hem och digitalt köpekontrakt',
    typicalDocuments: [
      'Köpekontrakt och tilläggsavtal',
      'Förmedlingsuppdrag och objektsbeskrivningar',
      'Depositionsavtal',
      'Tillträdesprotokoll och likvidavräkningar'
    ],
    ctaHeadline: 'Avsluta fler bostadsaffärer med KvikSign',
    ctaSubtext: 'Skapa ett gratiskonto med 5 credits – kom igång på under 2 minuter.',
    matrix: {
      kvikSign: 'Köpekontrakt & tilläggsavtal',
      kvikID: 'BankID- och ID-kontroll',
      kvikSend: 'Depositionsakter & mäklarhandlingar',
      kvikForm: 'Budgivning & kundkännedom'
    }
  },
  'advokater-juridisk-raadgivning': {
    name: 'Advokater & Juridisk rådgivning',
    title: 'Full beviskraft, PAdES LTV och säker klientkommunikation för jurister och advokater',
    shortDescription: 'Full beviskraft med PAdES LTV och eIDAS. Skicka sekretessavtal, fullmakter och rättegångshandlingar med spårbar revisionslogg och krypterad fildelning.',
    seoTitle: 'Advokater & Jurister – Digital signatur & eIDAS | KvikSign',
    metaDescription: 'Juridiskt bindande digitala signaturer med PAdES LTV och säker klientfildelning för advokatbyråer.',
    heroImageAlt: 'Advokatkontor med juridisk litteratur och digital signatur',
    typicalDocuments: [
      'Sekretessavtal (NDA) och samarbetsavtal',
      'Fullmakter och domstolshandlingar',
      'Förlikningsavtal och aktieägaravtal',
      'Testamenten, äktenskapsförord och gåvobrev',
      'GDPR- och PUB-dokumentation'
    ],
    ctaHeadline: 'Stärk er juridiska beviskraft idag',
    ctaSubtext: 'Testa KvikSign gratis med 5 credits – uppfyller eIDAS och GDPR till 100%.',
    matrix: {
      kvikSign: 'Avtal, fullmakter & förlikningar',
      kvikID: 'Klientverifiering & KYC',
      kvikSend: 'Förtroliga handlingar & rättegångsakter',
      kvikForm: 'Intag av klientinformation'
    }
  },
  'hr-rekruttering': {
    name: 'HR & Rekrytering',
    title: 'Effektiv och säker dokumenthantering för moderna HR-team och rekryterare',
    shortDescription: 'Anställningsavtal signerade på mobilen inom minuter. Samla in basuppgifter med KvikForm och skicka lönespecifikationer säkert med BankID-skydd via KvikSend.',
    seoTitle: 'HR & Rekrytering – Digital signatur för anställningsavtal | KvikSign',
    metaDescription: 'Snabbare anställningsprocesser med digital signering av anställningsavtal och säker distribution av personaldokument.',
    heroImageAlt: 'HR-specialist som hanterar digitala anställningsavtal',
    typicalDocuments: [
      'Anställningsavtal och chefsavtal',
      'Tilläggsavtal, löneändringar och befordringar',
      'Sekretessavtal och konkurrensklausuler',
      'Personalhandböcker och IT-policygodkännanden',
      'Uppsägnings- och avgångsöverenskommelser'
    ],
    ctaHeadline: 'Ge era nyanställda en modern onboarding-upplevelse',
    ctaSubtext: 'Starta gratis med 5 credits – inga bindningstider eller fasta avgifter.',
    matrix: {
      kvikSign: 'Anställningsavtal & policydokument',
      kvikID: 'Identitetskontroll av nyanställda',
      kvikSend: 'Lönespecifikationer & intyg',
      kvikForm: 'Medarbetaruppgifter & onboarding'
    }
  },
  'finans-forsikring-leasing': {
    name: 'Finans, Försäkring & Leasing',
    title: 'Digital signering av låneavtal, leasingkontrakt och automatiserad penningtvättskontroll',
    shortDescription: 'Accelerera kreditgodkännanden och leasingkontrakt. Efterlev Finansinspektionens krav med automatisk eID- och personvalidering och juridiskt bindande e-signatur.',
    seoTitle: 'Finans & Leasing – Digital signatur & penningtvättskontroll | KvikSign',
    metaDescription: 'Säkra finansiella avtal med digital signatur, BankID och automatiserad penningtvättskontroll för finansbolag och försäkringsförmedlare.',
    heroImageAlt: 'Finansrådgivare som granskar leasingkontrakt digitalt',
    typicalDocuments: [
      'Kredit- och låneavtal',
      'Leasingkontrakt och finansieringsavtal',
      'Borgensförbindelser och pantsättningar',
      'Försäkringspoliser och villkorsgodkännanden',
      'Fullständiga KYC- och eID-kontroller'
    ],
    ctaHeadline: 'Optimera er kreditgivning och kundonboarding',
    ctaSubtext: 'Prova KvikSign gratis med 5 credits – full överensstämmelse med eIDAS och finansregler.',
    matrix: {
      kvikSign: 'Låneavtal, leasing & fullmakter',
      kvikID: 'Penningtvättskontroll & eID-validering',
      kvikSend: 'Känsliga finansiella dokument',
      kvikForm: 'Låneansökningar & hälsodeklarationer'
    }
  },
  'sundhed-behandling-klinikker': {
    name: 'Hälsa, Vård & Kliniker',
    title: 'Säker patientkommunikation, digitala samtycken och journalhantering för kliniker',
    shortDescription: '100% GDPR-efterlevnad för journaler och samtycken. Samla in hälsodeklarationer digitalt innan konsultation och skicka konfidentiella provsvar krypterat.',
    seoTitle: 'Vård & Kliniker – Säkra patientformulär & e-signatur | KvikSign',
    metaDescription: 'Samla in patienthälsodeklarationer och behandlingssamtycken säkert med BankID och KvikSign. 100% GDPR-säkert.',
    heroImageAlt: 'Läkare och vårdpersonal som använder digitala samtyckesformulär',
    typicalDocuments: [
      'Behandlingssamtycken och operationsgodkännanden',
      'Digitala hälsodeklarationer',
      'GDPR-samtycken och journalfullmakter',
      'Kostnadsförslag och betalningsförbindelser',
      'Remisser och provsvar med BankID-access'
    ],
    ctaHeadline: 'Skydda känsliga patientdata med KvikSign',
    ctaSubtext: 'Testa KvikSign och KvikSend gratis – enkel och GDPR-säker vårdadministration.',
    matrix: {
      kvikSign: 'Behandlingssamtycken & GDPR',
      kvikID: 'Patientverifiering med e-legitimation',
      kvikSend: 'Journaler, provsvar & remisser',
      kvikForm: 'Digitala hälsodeklarationer'
    }
  },
  'haandvaerk-byggeri': {
    name: 'Hantverk & Byggbransch',
    title: 'Digitala offerter, entreprenadavtal och godkännande av extraarbeten i fält',
    shortDescription: 'Få offerter och ändringsarbeten godkända direkt på byggarbetsplatsen. Undvik obetalda extraarbeten med juridiskt bindande digital accept via SMS eller e-post.',
    seoTitle: 'Hantverk & Bygg – Digital offertaccept & entreprenadavtal | KvikSign',
    metaDescription: 'Få offerter och ändringsarbeten signerade direkt på mobilen av beställare och byggherrar med KvikSign.',
    heroImageAlt: 'Byggledare och hantverkare med digital ritning och avtal',
    typicalDocuments: [
      'Entreprenad- och hantverkaravtal',
      'Offertaccepter och tilläggsavtal (ÄTA)',
      'Överlämningsprotokoll och garantisedlar',
      'Underleverantörsavtal och sekretessavtal',
      'Dokumentation för egenkontroll och arbetsmiljö'
    ],
    ctaHeadline: 'Få betalt för alla era extraarbeten i tid',
    ctaSubtext: 'Skapa ett konto gratis med 5 credits – signera offerter direkt från byggarbetsplatsen.',
    matrix: {
      kvikSign: 'Offerter, entreprenadavtal & ändringsorder',
      kvikID: 'Underleverantörsvalidering',
      kvikSend: 'Ritningar, projekteringsfiler & foton',
      kvikForm: 'Kvalitetssäkring & dagrapporter'
    }
  },
  'bilforhandlere-leasing': {
    name: 'Bilhandlare & Fordonsleasing',
    title: 'Digitala slutkontrakt, leasingavtal och provkörningsgodkännande för bilhandlare',
    shortDescription: 'Digitala slutkontrakt och leasingavtal i bilhallen eller på distans. Verifiera körkort och identitet innan provkörning med KvikID och skydda mot bedrägerier.',
    seoTitle: 'Bilhandlare & Fordon – Digitala köpeavtal & leasing | KvikSign',
    metaDescription: 'Stäng bilaffärer snabbare med digital signatur på köpeavtal och omedelbar identitetskontroll inför provkörning.',
    heroImageAlt: 'Modern bilhall med digitala fordonskontrakt',
    typicalDocuments: [
      'Köpeavtal och slutavräkningar för personbilar och transportbilar',
      'Privatleasing- och företagsleasingkontrakt',
      'Provkörningsavtal och körkortsvalidering',
      'Inbytesdeklarationer och fullmakter',
      'Garantibevis och leveransgodkännanden'
    ],
    ctaHeadline: 'Gör bilaffärer snabbare och säkrare',
    ctaSubtext: 'Kom igång gratis med 5 credits – signera bilkontrakt på nolltid.',
    matrix: {
      kvikSign: 'Köpeavtal, leasing & finansiering',
      kvikID: 'Körkortskontroll & provkörningsavtal',
      kvikSend: 'Registreringsbevis & fakturor',
      kvikForm: 'Inbytesvärdering & serviceorder'
    }
  },
  'medlemsorganisationer-foreninger': {
    name: 'Föreningar & Styrelser',
    title: 'Digital signatur för styrelseprotokoll, årsmötesbeslut och föreningsdokument',
    shortDescription: 'Effektivisera styrelsearbetet och stämmoprotokoll. Samla in godkännanden från alla styrelsemedlemmar digitalt med certifierad revisionslogg och arkivering.',
    seoTitle: 'Styrelser & Föreningar – Digital signering av protokoll | KvikSign',
    metaDescription: 'Signera stämmoprotokoll, styrelsebeslut och stadgeändringar digitalt med BankID och KvikSign.',
    heroImageAlt: 'Styrelsemöte med digital protokollsignering',
    typicalDocuments: [
      'Stämmoprotokoll och styrelseprotokoll',
      'Styrelsebeslut och cirkulationsbeslut',
      'Stadgar och ordningsregler',
      'Fullmakter för föreningsstämmor',
      'Föreningsavtal och leverantörskontrakt'
    ],
    ctaHeadline: 'Förenkla styrelsearbetet för alla ledamöter',
    ctaSubtext: 'Starta gratis med 5 credits – slipp jaga fysiska namnteckningar.',
    matrix: {
      kvikSign: 'Stämmoprotokoll & styrelsebeslut',
      kvikID: 'Verifiering av firmatecknare',
      kvikSend: 'Konfidentiella styrelsematerial',
      kvikForm: 'Medlemsansökningar & fullmakter'
    }
  },
  'konsulenthuse-it': {
    name: 'Konsultbolag & IT-tjänster',
    title: 'Digitala konsultavtal, NDA och säker distribution av API-nycklar och källkod',
    shortDescription: 'Snabbare avslut på konsultuppdrag och ramavtal. Få NDA och personuppgiftsbiträdesavtal (DPA) signerade digitalt och dela API-nycklar och inloggningar säkert.',
    seoTitle: 'Konsulter & IT – Digitala uppdragsavtal, NDA & DPA | KvikSign',
    metaDescription: 'Stäng IT- och konsultavtal snabbare med digital signering, säker överföring av inloggningar och automatiserad kundonboarding.',
    heroImageAlt: 'IT-konsulter som samarbetar kring digitala kontrakt och kod',
    typicalDocuments: [
      'Ramavtal och konsultkontrakt (Master Services Agreements)',
      'Personuppgiftsbiträdesavtal (PUB/DPA)',
      'Service Level Agreements (SLA)',
      'Sekretessavtal (NDA)',
      'Projektbriefingar och godkännande av delleveranser'
    ],
    ctaHeadline: 'Stäng fler konsultavtal snabbare med KvikSign',
    ctaSubtext: 'Testa KvikSign gratis med 5 credits – kom igång på under 2 minuter.',
    matrix: {
      kvikSign: 'Ramavtal, uppdragsbekräftelser & NDA',
      kvikID: 'Kundvalidering',
      kvikSend: 'Källkod, API-nycklar & behörigheter',
      kvikForm: 'Briefing & projektstartformulär'
    }
  }
};

export function getLocalizedIndustryData(item: IndustryDetail, lang: 'da' | 'en' | 'sv' = 'da') {
  if (lang === 'en') {
    return {
      name: item.englishName,
      title: item.englishTitle,
      shortDescription: item.englishShortDescription,
      seoTitle: item.englishSeoTitle || item.englishTitle,
      metaDescription: item.englishMetaDescription,
      heroImageAlt: item.englishHeroImageAlt,
      typicalDocuments: item.englishTypicalDocuments,
      matrix: item.englishMatrix,
      ctaHeadline: item.englishCtaHeadline || item.ctaHeadline,
      ctaSubtext: item.englishCtaSubtext || item.ctaSubtext
    };
  }
  if (lang === 'sv') {
    const sv = SWEDISH_INDUSTRIES_MAP[item.slug];
    return {
      name: sv?.name || item.name,
      title: sv?.title || item.title,
      shortDescription: sv?.shortDescription || item.shortDescription,
      seoTitle: sv?.seoTitle || item.seoTitle,
      metaDescription: sv?.metaDescription || item.metaDescription,
      heroImageAlt: sv?.heroImageAlt || item.heroImageAlt,
      typicalDocuments: sv?.typicalDocuments || item.typicalDocuments,
      matrix: sv?.matrix || item.matrix,
      ctaHeadline: sv?.ctaHeadline || item.ctaHeadline,
      ctaSubtext: sv?.ctaSubtext || item.ctaSubtext
    };
  }
  return {
    name: item.name,
    title: item.title,
    shortDescription: item.shortDescription,
    seoTitle: item.seoTitle,
    metaDescription: item.metaDescription,
    heroImageAlt: item.heroImageAlt,
    typicalDocuments: item.typicalDocuments,
    matrix: item.matrix,
    ctaHeadline: item.ctaHeadline,
    ctaSubtext: item.ctaSubtext
  };
}
